// @flow
import type {Node} from 'react';
import {getBoolVariation, LD_FLAGS} from 'modules/feature-flags';
import type {RouteType} from 'types';
import {withRouter} from 'components/withRouter';
import {useParams, Navigate, Outlet} from 'react-router-dom';

import {NAVIGATION_NAMES, NAVIGATION_ICONS} from 'constants/Navigation';
import {Roles} from '@wellstone-solutions/common';

import {AnnouncementsPage} from './authorized/Announcements';

import OverviewPage from './authorized/Overview';
import {MyConversations} from 'modules/chat';
import {CalendarPage} from './authorized/Calendar';
import {SignalsPage} from './authorized/Signals';
import AdminDetailPage from './authorized/AdminDetail';
import GroupDetailPage from './authorized/GroupDetail';
import {
  DocumentationLanding,
  DocumentationRoot,
  DocumentationTabsRoot,
  SessionDocumentsTab,
  NotesTab,
  NewNotePage,
} from 'modules/documentation';
import {
  MemberRoot,
  MemberDetail,
  MemberDetailTabs,
  AllMembersTab,
  UnassignedMembersTab,
  PublicInvitesTab,
} from 'modules/member';
import {GroupRoot, New as NewGroup, Edit as EditGroup} from 'modules/group';
import {StaffRoot, AllStaffTab, UnassignedStaffTab} from 'modules/staff';
import {StoriesRoot} from 'modules/stories';
import {StoryNew} from 'modules/stories';
import {StoryLoader} from 'modules/stories';
import {NewCalendarEvent} from 'modules/calendar';
import {CalendarDetailPage} from 'modules/calendar';
import {DashboardRoot} from 'modules/dashboard';
import {
  ResourcesRoot,
  SavedResources,
  AllResources,
  NewResource,
  EditResource,
} from 'modules/resources/v2';

const RedirectToNotes = (): Node => <Navigate to="notes" replace />;

export const getAuthorizedRoutes = (meStore): Array<RouteType> => {
  const accessToStories = getBoolVariation(LD_FLAGS.STORY_MANAGER);
  const accessToResources = true;
  const accessToResourceAdmin = getBoolVariation(LD_FLAGS.RESOURCE_ADMIN);
  const accessToSignals = getBoolVariation(LD_FLAGS.SIGNAL_BRIDGE);
  const accessToSessionDocumentation = getBoolVariation(
    LD_FLAGS.PEERS_DOCUMENTATION,
  );
  const enableContactNotes = getBoolVariation(LD_FLAGS.CONTACT_NOTES);
  const enableUnassignedUsers = getBoolVariation(
    LD_FLAGS.ENABLE_UNASSIGNED_USERS,
  );
  let authorizedRoutes = [
    {
      showInMenu: true,
      path: 'conversations',
      name: NAVIGATION_NAMES.conversations,
      icon: NAVIGATION_ICONS.conversations,
      Component: MyConversations,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
    },
    {
      showInMenu: true,
      path: 'calendar',
      name: NAVIGATION_NAMES.calendar,
      icon: NAVIGATION_ICONS.calendar,
      Component: CalendarPage,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
      isBottomRuled: true,
    },
    {
      path: 'calendar/calendar-event/new',
      name: NAVIGATION_NAMES.newEvent,
      icon: NAVIGATION_ICONS.newEvent,
      Component: NewCalendarEvent,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
    },
    {
      path: 'calendar/calendar-event/:id',
      name: NAVIGATION_NAMES.detailEvent,
      icon: NAVIGATION_ICONS.detailEvent,
      Component: CalendarDetailPage,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
    },
    {
      path: 'dashboard/*',
      Component: () => {
        // Redirect nested dashboard routes
        const {'*': splat} = useParams();
        return <Navigate to={`/${splat}`} />;
      },
    },
    {
      showInMenu: true,
      path: 'dashboard',
      name: NAVIGATION_NAMES.dashboard,
      icon: NAVIGATION_ICONS.dashboard,
      Component: DashboardRoot,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
    },
    {
      path: 'announcements/:group_id',
      name: NAVIGATION_NAMES.announcements,
      icon: NAVIGATION_ICONS.announcements,
      Component: AnnouncementsPage,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
    },
    {
      showInMenu: true,
      path: 'announcements',
      name: NAVIGATION_NAMES.announcements,
      icon: NAVIGATION_ICONS.announcements,
      Component: AnnouncementsPage,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
    },
    {
      path: 'members/:member',
      name: NAVIGATION_NAMES.memberDetail,
      icon: NAVIGATION_ICONS.memberDetail,
      Component: MemberDetail,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
      children: [
        {
          path: 'activity',
          name: NAVIGATION_NAMES.memberDetailActivityTab,
          icon: NAVIGATION_ICONS.memberDetailActivityTab,
          Component: MemberDetailTabs.ActivityTabContent,
          allowed: [
            Roles.SUPER_ADMIN,
            Roles.ADMIN,
            Roles.COUNSELOR,
            Roles.PEER,
          ],
        },
        {
          path: 'habit-schedule',
          name: NAVIGATION_NAMES.memberDetailHabitScheduleTab,
          icon: NAVIGATION_ICONS.memberDetailHabitScheduleTab,

          Component: MemberDetailTabs.HabitScheduleTabContent,
          allowed: [
            Roles.SUPER_ADMIN,
            Roles.ADMIN,
            Roles.COUNSELOR,
            Roles.PEER,
          ],
        },
        {
          path: 'session-docs',
          name: NAVIGATION_NAMES.memberDetailSessionDocTab,
          icon: NAVIGATION_ICONS.memberDetailSessionDocTab,
          Component: MemberDetailTabs.SessionDocTabContent,
          allowed: [
            Roles.SUPER_ADMIN,
            Roles.ADMIN,
            Roles.COUNSELOR,
            Roles.PEER,
          ],
          isDisabled: !accessToSessionDocumentation,
        },
        {
          path: 'permissions',
          name: NAVIGATION_NAMES.programsAndGroups,
          icon: NAVIGATION_ICONS.memberDetailActivityTab,
          Component: MemberDetailTabs.ProgramsAndGroupsTabContent,
          allowed: [Roles.SUPER_ADMIN, Roles.ADMIN],
        },
        {
          index: true,
          name: NAVIGATION_NAMES.memberDetailGeneralTab,
          icon: NAVIGATION_ICONS.memberDetailGeneralTab,
          Component: MemberDetailTabs.GeneralTabContent,
          allowed: [
            Roles.SUPER_ADMIN,
            Roles.ADMIN,
            Roles.COUNSELOR,
            Roles.PEER,
          ],
        },
      ],
    },
    {
      showInMenu: true,
      path: 'members',
      name: NAVIGATION_NAMES.members,
      icon: NAVIGATION_ICONS.members,
      Component: MemberRoot,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
      children: [
        {
          index: true,
          Component: AllMembersTab,
          allowed: [
            Roles.SUPER_ADMIN,
            Roles.ADMIN,
            Roles.COUNSELOR,
            Roles.PEER,
          ],
        },
        ...(enableUnassignedUsers
          ? [
              {
                path: 'unassigned',
                Component: UnassignedMembersTab,
                allowed: [
                  Roles.SUPER_ADMIN,
                  Roles.ADMIN,
                  Roles.COUNSELOR,
                  Roles.PEER,
                ],
              },
            ]
          : []),
        {
          // Not flag-gated. Access is enforced server-side (admin/super_admin);
          // non-admins receive a 403 and see the tab's error state.
          path: 'public-invites',
          Component: PublicInvitesTab,
          allowed: [
            Roles.SUPER_ADMIN,
            Roles.ADMIN,
            Roles.COUNSELOR,
            Roles.PEER,
          ],
        },
      ],
    },
  ];

  if (accessToSessionDocumentation || enableContactNotes) {
    authorizedRoutes = [
      ...authorizedRoutes,
      {
        showInMenu: true,
        path: 'documentation',
        name: NAVIGATION_NAMES.documentation,
        icon: NAVIGATION_ICONS.documentation,
        allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
        ...(enableContactNotes
          ? {
              Component: Outlet,
              children: [
                {
                  Component: DocumentationTabsRoot,
                  children: [
                    {
                      index: true,
                      Component: accessToSessionDocumentation
                        ? SessionDocumentsTab
                        : RedirectToNotes,
                      allowed: [
                        Roles.SUPER_ADMIN,
                        Roles.ADMIN,
                        Roles.COUNSELOR,
                        Roles.PEER,
                      ],
                    },
                    {
                      path: 'notes',
                      Component: NotesTab,
                      allowed: [
                        Roles.SUPER_ADMIN,
                        Roles.ADMIN,
                        Roles.COUNSELOR,
                        Roles.PEER,
                      ],
                    },
                  ],
                },
                {
                  path: 'notes/new',
                  name: NAVIGATION_NAMES.newNote,
                  icon: NAVIGATION_ICONS.documentation,
                  Component: NewNotePage,
                  allowed: [
                    Roles.SUPER_ADMIN,
                    Roles.ADMIN,
                    Roles.COUNSELOR,
                    Roles.PEER,
                  ],
                },
              ],
            }
          : {Component: DocumentationLanding}),
      },
    ];
  }

  if (accessToSessionDocumentation) {
    authorizedRoutes = [
      ...authorizedRoutes,
      {
        path: 'members/:member/documentation',
        name: NAVIGATION_NAMES.documentation,
        icon: NAVIGATION_ICONS.documentation,
        Component: withRouter(DocumentationRoot),
        allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
      },
      {
        path: 'members/:member/documentation/:id',
        name: NAVIGATION_NAMES.documentation,
        icon: NAVIGATION_ICONS.documentation,
        Component: withRouter(DocumentationRoot),
        allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
      },
    ];
  }

  authorizedRoutes = [
    ...authorizedRoutes,
    {
      path: 'groups/:group',
      name: NAVIGATION_NAMES.groupDetail,
      icon: NAVIGATION_ICONS.groupDetail,
      Component: GroupDetailPage,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
    },
    {
      showInMenu: true,
      path: 'groups',
      name: NAVIGATION_NAMES.groups,
      icon: NAVIGATION_ICONS.groups,
      Component: GroupRoot,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
    },
    {
      path: 'groups/new',
      name: NAVIGATION_NAMES.groups,
      icon: NAVIGATION_ICONS.groups,
      Component: NewGroup,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN],
    },
    {
      path: 'groups/:group/edit',
      name: NAVIGATION_NAMES.groupDetail,
      icon: NAVIGATION_ICONS.groupDetail,
      Component: EditGroup,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
    },
    {
      path: 'staff/:admin',
      name: NAVIGATION_NAMES.staffDetail,
      icon: NAVIGATION_ICONS.staffDetail,
      Component: withRouter(AdminDetailPage),
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
    },
    {
      showInMenu: true,
      path: 'staff',
      name: NAVIGATION_NAMES.staff,
      icon: NAVIGATION_ICONS.staff,
      Component: StaffRoot,
      allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
      isBottomRuled: accessToStories && !meStore.isSuperAdmin,
      children: [
        {
          index: true,
          Component: AllStaffTab,
          allowed: [
            Roles.SUPER_ADMIN,
            Roles.ADMIN,
            Roles.COUNSELOR,
            Roles.PEER,
          ],
        },
        ...(enableUnassignedUsers
          ? [
              {
                path: 'unassigned',
                Component: UnassignedStaffTab,
                allowed: [
                  Roles.SUPER_ADMIN,
                  Roles.ADMIN,
                  Roles.COUNSELOR,
                  Roles.PEER,
                ],
              },
            ]
          : []),
      ],
    },
    {
      showInMenu: true,
      path: 'activity',
      name: NAVIGATION_NAMES.activity,
      icon: NAVIGATION_ICONS.activity,
      Component: OverviewPage,
      allowed: [Roles.SUPER_ADMIN],
      isBottomRuled: accessToStories,
    },
  ];

  if (accessToResources) {
    authorizedRoutes = [
      ...authorizedRoutes,
      {
        showInMenu: true,
        path: 'resources',
        name: NAVIGATION_NAMES.resources,
        icon: NAVIGATION_ICONS.resources,
        Component: ResourcesRoot,
        allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
        children: [
          {
            index: true,
            name: NAVIGATION_NAMES.resourcesAll,
            icon: NAVIGATION_ICONS.resourcesAll,
            Component: AllResources,
            allowed: [
              Roles.SUPER_ADMIN,
              Roles.ADMIN,
              Roles.COUNSELOR,
              Roles.PEER,
            ],
          },
          {
            path: 'saved',
            name: NAVIGATION_NAMES.savedResources,
            icon: NAVIGATION_ICONS.savedResources,
            Component: SavedResources,
            allowed: [
              Roles.SUPER_ADMIN,
              Roles.ADMIN,
              Roles.COUNSELOR,
              Roles.PEER,
            ],
          },
        ],
      },
    ];

    if (accessToResourceAdmin) {
      authorizedRoutes = [
        ...authorizedRoutes,
        {
          showInMenu: false,
          path: 'resources/new',
          name: NAVIGATION_NAMES.resources,
          icon: NAVIGATION_ICONS.resources,
          Component: NewResource,
          allowed: [
            Roles.SUPER_ADMIN,
            Roles.ADMIN,
            Roles.COUNSELOR,
            Roles.PEER,
          ],
        },
        {
          showInMenu: false,
          path: 'resources/:id/edit',
          name: NAVIGATION_NAMES.resources,
          icon: NAVIGATION_ICONS.resources,
          Component: EditResource,
          allowed: [
            Roles.SUPER_ADMIN,
            Roles.ADMIN,
            Roles.COUNSELOR,
            Roles.PEER,
          ],
        },
      ];
    }
  }

  if (accessToSignals) {
    authorizedRoutes = [
      ...authorizedRoutes,
      {
        showInMenu: true,
        path: 'signals',
        name: NAVIGATION_NAMES.signals,
        icon: NAVIGATION_ICONS.signals,
        Component: SignalsPage,
        allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
      },
    ];
  }

  if (accessToStories) {
    authorizedRoutes = [
      ...authorizedRoutes,
      {
        showInMenu: true,
        path: 'stories',
        name: NAVIGATION_NAMES.stories,
        icon: NAVIGATION_ICONS.stories,
        Component: StoriesRoot,
        allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
      },
      {
        showInMenu: false,
        path: 'stories/new',
        name: NAVIGATION_NAMES.storyNew,
        icon: NAVIGATION_ICONS.storyNew,
        Component: StoryNew,
        allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
      },
      {
        showInMenu: false,
        path: 'stories/:id',
        name: NAVIGATION_NAMES.storyEdit,
        icon: NAVIGATION_ICONS.storyEdit,
        Component: StoryLoader,
        allowed: [Roles.SUPER_ADMIN, Roles.ADMIN, Roles.COUNSELOR, Roles.PEER],
      },
    ];
  }

  const defaultPage = '/dashboard';
  authorizedRoutes.push({
    Component: (): Node => <Navigate to={defaultPage} />,
    isProtected: true,
    path: '*',
    allowed: [],
  });

  return authorizedRoutes;
};
